/**
 * One row of the form: a label, whether it is required, and the control — or the reason the trainer
 * cannot answer it yet.
 *
 * ── A locked field STAYS, and that is the whole reason this component exists ─────────────────
 * View ADR §12. A field that appears once the one above it is answered moves everything below it,
 * and a trainer who cannot see the question cannot plan the four answers. So a locked field is
 * drawn in its place, dimmed, with the reason under it — and the CONTROL inside it stays focusable,
 * because `disabled` would drop it out of the tab order and take the explanation with it.
 *
 * The reason's id is handed back to the caller rather than wired here: the control that has to be
 * `aria-describedby` it is the caller's (a radio group's fieldset, an input, a grid), and this
 * component has no way to reach it. It travels with `locked` as one object, because no control in
 * this form ever wants one without the other.
 *
 * ── The field is its own ADDRESS ─────────────────────────────────────────────────────────────
 * A refused primary puts the caret in whichever field is blocking the send, and it finds it by
 * `data-assign-field`. That attribute is written here, from `name`, rather than by each of the five
 * callers: five hand-copied wrappers is five chances for one of them to be spelled differently, and
 * the failure is silent — the press simply does nothing.
 */

import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { AssignField as AssignFieldName, FieldGate } from '../model/assignForm';

import styles from './AssignPlanDialog.module.css';

/** What every control in this form needs to know about its own gate. */
export interface FieldLock {
  readonly locked: boolean;
  /** The reason's id when the field is locked, so the control can describe itself with it. */
  readonly reasonId: string | null;
  /**
   * The help sentence's id when one is drawn, for the same reason the reason's is handed back.
   *
   * The start field's help says what a mid-week start STRIKES — a consequence of the very choice
   * the grid below it is for — and a sentence sitting beside a control describes nothing unless the
   * control names it. Stable across the sentence changing, so a reader following the description is
   * not re-pointed at a new node every time the count moves.
   */
  readonly helpId: string | null;
}

export interface AssignFieldProps {
  /** Which question this is — the address a refused primary focuses by. */
  readonly name: AssignFieldName;
  readonly gate: FieldGate;
  /** The label's own words — already translated, because the caller knows which field this is. */
  readonly label: string;
  readonly required?: boolean;
  /** The one-line note under the control when it IS answerable. */
  readonly help?: ReactNode;
  readonly children: (lock: FieldLock) => ReactNode;
}

export function AssignField({
  name,
  gate,
  label,
  required = false,
  help,
  children,
}: AssignFieldProps) {
  const { t } = useTranslation();
  const reasonId = useId();
  const helpId = useId();

  if (gate.state === 'absent') return null;
  const locked = gate.state === 'locked';
  const drawsHelp = !locked && help !== undefined;

  return (
    <div className={`${String(styles.field)} ${locked ? String(styles.fieldLocked) : ''}`}>
      <span className={styles.fieldLabel}>
        {label}
        {required && <span className={styles.fieldRequired}>{t('web:assign.field.required')}</span>}
      </span>
      <div data-assign-field={name}>
        {children({
          locked,
          reasonId: locked ? reasonId : null,
          helpId: drawsHelp ? helpId : null,
        })}
      </div>
      {/* Always in the tree, so the id a locked control names never points at nothing. */}
      <span id={reasonId} className={locked ? styles.fieldReason : styles.fieldHidden}>
        {gate.state === 'locked' ? t(`web:assign.locked.${gate.reason}`) : ''}
      </span>
      {drawsHelp && (
        <span id={helpId} className={styles.fieldHelp}>
          {help}
        </span>
      )}
    </div>
  );
}
